import { Link, useLocation } from "wouter";
import { ArrowLeft, Edit, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";

interface Product {
  id: string;
  sku: string;
  name: string;
  productClass: string;
  itemType: string;
  status: string;
  updatedAt: string | null;
  createdAt: string;
  category?: { id: string; name: string };
  categoryName?: string;
  account?: { id: string; name: string };
}

interface ProductHeaderProps {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
  /** flag chips driven by other tabs (file count badge, new-version flag, etc.) */
  flags?: React.ReactNode;
}

const ITEM_TYPE_LABELS: Record<string, string> = {
  inventory: "Inventory",
  non_inventory: "Non-Inventory",
  service: "Service",
  bundle: "Bundle",
};

function statusDotClass(status: string): string {
  switch (status) {
    case "active": return "bg-green-500";
    case "inactive":
    case "discontinued": return "bg-red-500";
    case "prospect": return "bg-yellow-500";
    default: return "bg-gray-400";
  }
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day >= 1) return `${day} day${day === 1 ? "" : "s"} ago`;
  if (hr >= 1) return `${hr}h ago`;
  if (min >= 1) return `${min}m ago`;
  return "just now";
}

export function ProductHeader({ product, onEdit, onDelete, flags }: ProductHeaderProps) {
  const [, setLocation] = useLocation();
  const categoryName = product.categoryName || product.category?.name || null;

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={() => setLocation("/products")}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Back to products"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground font-normal">SKU:</span>
          <span className="font-mono">{product.sku}</span>
          <span className="text-muted-foreground font-normal">/</span>
          {product.productClass === "custom" ? (
            product.account ? (
              <Link href={`/accounts/${product.account.id}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline">
                {product.account.name}
              </Link>
            ) : (
              <span className="text-blue-600 dark:text-blue-400">Custom</span>
            )
          ) : (
            <span>Stock</span>
          )}
          <span className="text-muted-foreground font-normal">/</span>
          <span>{product.name}</span>
        </h1>
      </div>

      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          Updated {relativeTime(product.updatedAt || product.createdAt)}
        </span>
        <Badge variant="outline" className="capitalize">
          {product.productClass}
        </Badge>
        {categoryName && (
          <Badge variant="outline">{categoryName}</Badge>
        )}
        <Badge variant="outline">
          {ITEM_TYPE_LABELS[product.itemType] || product.itemType}
        </Badge>
        <Badge variant="outline" className="capitalize flex items-center gap-1.5">
          <span className={cn("h-2 w-2 rounded-full", statusDotClass(product.status))} />
          {product.status}
        </Badge>
        {flags}
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Edit className="h-3.5 w-3.5 mr-1.5" />
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onDelete}
              className="text-destructive focus:text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
